import React, { ReactElement, cloneElement } from 'react';
import {
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
  Breakpoints,
  useIsMobile,
  useTheme,
} from '@vw-marketing/us-components';
import { useLocation } from 'react-router-dom';

import ChevronRight from '../icons/ChevronRight';
import LinkExternal from '../icons/LinkExternal';
import { NavigationItemContainer, StyledTitleContainer } from './styles';
import { useFlags } from 'launchdarkly-react-client-sdk';

const NavigationItem = ({
  externalLink,
  icon,
  iconWhenSelected,
  onClick,
  path,
  text,
  title,
  disabled = false,
}: {
  externalLink?: string;
  icon?: ReactElement<{ filled: boolean }>;
  iconWhenSelected?: ReactElement<{ filled: boolean }>;
  onClick?: () => void;
  path?: string;
  text: string | React.JSX.Element[];
  title?: string;
  disabled?: boolean;
}) => {
  const location = useLocation();

  const { ciGuidelinesRollout } = useFlags();
  const isDesktop: boolean = !useIsMobile(Breakpoints.b1280);
  const theme = useTheme();

  const isItemSelected: boolean = path ? location.pathname.includes(path) : false;

  const renderIcon = () => {
    if (!icon) return null;

    return cloneElement(icon, {
      filled: location.pathname.includes(path || ""),
    });
  };

  const handleClick = () => {
    if (disabled) return;

    onClick?.();

    if (externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <NavigationItemContainer
      aria-disabled={disabled}
      disabled={disabled}
      hasTitle={!!title}
      isSelected={isItemSelected}
      flag={ciGuidelinesRollout}
      onClick={handleClick}
    >
      {isItemSelected && iconWhenSelected
        ? iconWhenSelected
        : icon && renderIcon()}
      <StyledTitleContainer hasTitle={!!title} isSelected={isItemSelected}>
        {title && (
          <TextCustom
            appearance={TextAppearance.label100}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                color: TextColor.secondary,
                fontSize: TextSize.TextSize12,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight16,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize14,
              },
            }}
          >
            {title}
          </TextCustom>
        )}
        <TextCustom
          appearance={TextAppearance.copy200}
          tag={TextTag.p}
          style={{
            [Breakpoints.default]: {
              color: TextColor.active,
              fontWeight:
                path == location.pathname
                  ? TextWeight.bold
                  : TextWeight.regular,
              fontSize: TextSize.TextSize16,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
            [Breakpoints.b1280]: {
              fontSize: TextSize.TextSize20,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {text}
        </TextCustom>
      </StyledTitleContainer>
      {externalLink ? (
        <LinkExternal aria-hidden color={theme.colors.content.secondary} />
      ) : (
        !isDesktop && <ChevronRight aria-hidden iconSize={16} color={theme.colors.content.secondary} />
      )}
    </NavigationItemContainer>
  );
};

export default NavigationItem;
